import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { ButtonHTMLAttributes, FunctionComponent, ReactElement, ReactNode } from 'react'

import { Text } from '../Typography'
import { ACCESSORY_POSITIONS } from './constants'
import styles from './style.module.scss'

type AccessoryPositionsType = typeof ACCESSORY_POSITIONS
type AccessoryPositionsKeys = keyof AccessoryPositionsType
export type AccessoryPositions = AccessoryPositionsType[AccessoryPositionsKeys]

export interface BaseCellProps {
  asset?: ReactNode
  label: string | JSX.Element | (() => any)
  body?: ReactNode | (() => ReactNode)
  accessory?: ReactNode
  accessoryPosition?: AccessoryPositions
  disabled?: boolean
  breakout?: boolean
  lessHorizontalPadding?: boolean
  ariaLabelledby?: string
  ariaLabel?: string
  as?: React.ElementType
  testNameSpace?: string
}

export interface InteractiveCellProps extends ButtonHTMLAttributes<HTMLButtonElement>, BaseCellProps {
  testNameSpace?: string
  breakout?: boolean
}

const hasTestId = (testNameSpace?: string): testNameSpace is string => {
  return typeof testNameSpace === 'string' && testNameSpace.length > 0
}

const getTestIdProp = (testNameSpace?: string, suffix?: string): { 'data-testid': string } | {} => {
  if (hasTestId(testNameSpace)) {
    const computedSuffix = suffix ? `-${suffix}` : ''
    return { 'data-testid': `${testNameSpace}-cell${computedSuffix}` }
  }

  return {}
}

export const BaseCell: FunctionComponent<BaseCellProps> = ({
  asset,
  label,
  body,
  accessory,
  accessoryPosition = ACCESSORY_POSITIONS.TOP,
  disabled = false,
  breakout = false,
  lessHorizontalPadding = false,
  as,
  ariaLabelledby,
  ariaLabel,
  testNameSpace,
}) => {
  const renderLabel = (): ReactElement => (
    <Text size='M' renderAs='div' bold {...getTestIdProp(testNameSpace, 'label')} className={styles.label}>
      {isCallable(label) ? label() : label}
    </Text>
  )

  const renderBody = (): ReactElement | null => {
    if (!body) {
      return null
    }

    return <div className={styles.body}>{isCallable(body) ? body() : body}</div>
  }

  const renderAsset = (): ReactElement | null => {
    if (!asset) {
      return null
    }

    return (
      <div className={styles.asset} {...getTestIdProp(testNameSpace, 'asset')}>
        {asset}
      </div>
    )
  }

  const renderAccessory = (): ReactElement | null => {
    if (!accessory) {
      return null
    }

    return (
      <div className={styles.accessory} {...getTestIdProp(testNameSpace, 'accessory')}>
        {accessory}
      </div>
    )
  }

  const attributes = {
    'aria-labelledby': ariaLabelledby,
    'aria-label': ariaLabel,
    ...getTestIdProp(testNameSpace),
    ...(as === 'button' && disabled ? { disabled: true } : {}),
  }

  const componentClassName = clsx(
    styles.cell,
    disabled && styles.disabled,
    accessory && accessoryPosition && styles[`accessory${accessoryPosition}`],
    !body && styles.noBody,
    breakout && styles.breakout,
    lessHorizontalPadding && styles.lessHorizontalPadding
  )

  const Component = as ?? 'span'

  return (
    <Component className={componentClassName} {...attributes}>
      {renderAsset()}
      <div className={styles.content}>
        {renderLabel()}
        {renderBody()}
      </div>
      {renderAccessory()}
    </Component>
  )
}

export const InteractiveCell: FunctionComponent<InteractiveCellProps> = ({
  asset,
  label,
  body,
  accessory,
  accessoryPosition,
  disabled,
  breakout,
  lessHorizontalPadding = false,
  ariaLabelledby,
  ariaLabel,
  as,
  testNameSpace,
  ...buttonProps
}) => {
  const cellProps: BaseCellProps = {
    asset,
    label,
    body,
    accessory,
    accessoryPosition,
    ariaLabel,
    ariaLabelledby,
    disabled,
    lessHorizontalPadding,
    as,
    testNameSpace,
  }

  const attributes = {
    type: buttonProps.type ?? 'button',
    ...getTestIdProp(testNameSpace, 'button'),
    disabled,
    'aria-labelledby': buttonProps['aria-labelledby'] ?? ariaLabelledby,
    'aria-label': buttonProps['aria-label'] ?? ariaLabel,
  }

  const componentClassName = clsx(
    styles.interactive,
    disabled && styles.disabled,
    breakout && styles.breakout,
    lessHorizontalPadding && styles.lessHorizontalPadding
  )

  return (
    <button {...buttonProps} className={componentClassName} {...attributes}>
      <BaseCell {...cellProps} label={label} disabled={disabled} testNameSpace={testNameSpace} />
    </button>
  )
}

export type CellProps = BaseCellProps | InteractiveCellProps
export type CellComponent = FunctionComponent<CellProps>

const isInteractiveCell = (props: CellProps): props is InteractiveCellProps => {
  const onClick = (props as InteractiveCellProps).onClick
  return onClick !== undefined && isCallable(onClick)
}

export const Cell: CellComponent = (props) => {
  if (isInteractiveCell(props)) {
    return <InteractiveCell {...props} />
  }

  return <BaseCell {...props} />
}
